import PropTypes from 'prop-types';
import { Box, Grid, Typography } from '@material-ui/core';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatMonthLabel, getCurrentMonth } from './MonthPicker';

// Shared `sx` for the four stat tiles inside the invoice card — they all
// share the translucent panel look; only padding differs on the last one.
const styles = { summaryPanel: { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, padding: 1 } };

CompanyInvoiceCard.propTypes = {
  company: PropTypes.object.isRequired,
  billingMonth: PropTypes.string
};

CompanyInvoiceCard.defaultProps = {
  billingMonth: ''
};

export default function CompanyInvoiceCard({ company, billingMonth }) {
  // "Month-To-Date Cost" when viewing the current month (still being
  // accrued, no finalized invoice), "Company Bill" for any past month.
  // `billingMonth` is a YYYY-MM string (already normalized by MonthPicker
  // or by the caller); getCurrentMonth() produces the same shape.
  const isCurrentMonth = billingMonth && billingMonth === getCurrentMonth();
  const overlineLabel = isCurrentMonth ? 'Month-To-Date Cost' : 'Company Bill';

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
        color: '#fff',
        borderRadius: 2,
        padding: 2,
        boxShadow: (theme) => theme.customShadows?.z2 || '0 4px 12px rgba(0,0,0,0.12)',
        height: '100%'
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: '#d7e9fb', letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        {overlineLabel}
      </Typography>
      <Typography variant="h4" sx={{ my: 0.5, fontWeight: 500 }}>
        {formatCurrency(company.monthlyCharges)}
      </Typography>
      <Typography variant="caption" sx={{ color: '#d7e9fb', display: 'block' }}>
        {formatMonthLabel(billingMonth)}
      </Typography>

      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid item xs={6}>
          <Box sx={styles.summaryPanel}>
            <Typography variant="caption" sx={{ color: '#d7e9fb', display: 'block' }}>
              Platform Fee
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
              {formatCurrency(company.platformFee)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={styles.summaryPanel}>
            <Typography variant="caption" sx={{ color: '#d7e9fb', display: 'block' }}>
              Team Usage Cost
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
              {formatCurrency(company.teamUsageCost)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={styles.summaryPanel}>
            <Typography variant="caption" sx={{ color: '#d7e9fb', display: 'block' }}>
              Teams
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
              {company.teamsCount}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          {/* Slightly more padding so the longer "Billable Resources" value
              doesn't crowd the tile edges. */}
          <Box sx={styles.summaryPanel}>
            <Typography variant="caption" sx={{ color: '#d7e9fb', display: 'block' }}>
              Billable Resources
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
              {company.billableResources}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
